import { FC, useState, useContext, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './WishlistSidebar.module.scss'
import { useWishlistDrawer } from 'context/WishlistDrawerContext/WishlistDrawerContext'
import { useGetFavoriteMerchantQuery, useRemoveFavoriteMutation } from '../../api/apiFavoritesSlice'
import { CloseIcon, ShoppingCartIcon } from 'shared/assets'
import HearthFavoriteIcon from 'shared/assets/icons/Product/HeadFavoriteIcon'
import { SupplierContext } from 'context/SupplierContext'
import { ERoutePaths } from 'libraries/router/types'
import { usePostCartItemMutation, useGetCartItemsQuery } from 'entities/Order/api/apiCartSlice'
import { useCartDrawer } from 'context/BasketDriverContext/BasketDriverContext'
import { WishListIcon } from "assets"
import { ProductSlider } from 'components'
import { useFetchSupplierProductsListMutation } from '../../../ProductDetails/api/suppliers/apiProductSlice'
import { useAppDispatch, useAppSelector } from 'libraries/redux'
import { ProductSelectors } from 'store/product/selectors'
import { getBestSellers } from 'store/product/actions'
import type { FavoriteProduct } from '../../api/apiFavoritesSliceTypes'
import { showNotification, ToastVersions } from 'libraries/toastify'

interface SizeOption {
  name: string
  product_option_value_id: number
  warehouse_id: number
  quantity: number
  option_value_id: number
}

interface WishlistItemProps {
  item: any
  onAddToCart: (productId: number, selectedSize: SizeOption | null) => Promise<boolean>
  onRemoveFromWishlist: (productId: number) => void
}

const WishlistItem: FC<WishlistItemProps> = ({ item, onAddToCart, onRemoveFromWishlist }) => {
  const [selectedSize, setSelectedSize] = useState<SizeOption | null>(null)
  const [isAdded, setIsAdded] = useState(false)

  // Extract available sizes from item.options
  const availableSizes = useMemo(() => {
    const sizesList: SizeOption[] = []

    if (item?.options && Array.isArray(item.options)) {
      item.options.forEach((option: any) => {
        if (option?.warehouses && Array.isArray(option.warehouses)) {
          option.warehouses.forEach((warehouse: any) => {
            if (warehouse?.options && Array.isArray(warehouse.options)) {
              warehouse.options.forEach((sizeOption: any) => {
                // Only add sizes that are in stock and not marked as out of stock
                if (sizeOption.quantity > 0 && !sizeOption.is_marked_out_stock) {
                  sizesList.push({
                    name: sizeOption.name,
                    product_option_value_id: sizeOption.product_option_value_id,
                    warehouse_id: sizeOption.warehouse_id,
                    quantity: sizeOption.quantity,
                    option_value_id: sizeOption.option_value_id,
                  })
                }
              })
            }
          })
        }
      })
    }

    // Remove duplicates based on name (in case same size exists in multiple warehouses)
    const uniqueSizes = sizesList.reduce((acc: SizeOption[], current) => {
      const existingSize = acc.find(size => size.name === current.name)
      if (!existingSize) {
        acc.push(current)
      }

      return acc
    }, [])

    return uniqueSizes
  }, [item])

  const handleSizeSelect = (size: SizeOption) => {
    setSelectedSize(size)
    setIsAdded(false)
  }

  const handleAddToCart = async () => {
    if (selectedSize) {
      const success = await onAddToCart(item.id || item.product_id, selectedSize)
      if (success) {
        setIsAdded(true)
      }
    }
  }

  const handleRemoveFromWishlist = (e: React.MouseEvent) => {
    e.stopPropagation()
    onRemoveFromWishlist(item.id || item.product_id)
  }

  return (
    <div className={styles.wishlistItem}>
      <div className={styles.wishlistItem__imageWrapper}>
        <img src={item.thumb_large || item.thumb} alt={item.name} className={styles.wishlistItem__image} />
      </div>
      <div className={styles.wishlistItem__header}>
        <h3 className={styles.wishlistItem__name}>{item.name}</h3>
        <div className={styles.wishlistItem__heartIcon} onClick={handleRemoveFromWishlist}>
          <WishListIcon />
        </div>
      </div>
      <div className={styles.wishlistItem__price}>
        {item.special ? (
          <>
            <span className={styles.wishlistItem__price_special}>{item.special}</span>
            <span className={styles.wishlistItem__price_original}>{item.price}</span>
          </>
        ) : (
          <span>{item.price}</span>
        )}
      </div>
      <div className={styles.wishlistItem__sizes}>
        {availableSizes.length > 0 && availableSizes.map((size, index) => (
          <button
            key={`${size.name}-${size.option_value_id}-${index}`}
            className={`${styles.wishlistItem__sizeButton} ${
              selectedSize?.name === size.name ? styles.wishlistItem__sizeButton_active : ''
            }`}
            onClick={() => handleSizeSelect(size)}
          >
            {size.name}
          </button>
        ))}
      </div>
      {selectedSize ? (
        <button
          className={`${styles.wishlistItem__addButton} ${isAdded ? styles.wishlistItem__addButton_success : ''}`}
          onClick={handleAddToCart}
          disabled={isAdded}
        >
          {isAdded ? (
            <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M13.4137 0.23535C13.744 0.555894 13.7519 1.08347 13.4313 1.41373L5.66661 9.41373C5.50968 9.57542 5.29395 9.66667 5.06863 9.66667C4.8433 9.66667 4.62758 9.57542 4.47064 9.41373L0.23535 5.05009C-0.0851939 4.71984 -0.0773196 4.19226 0.252938 3.87171C0.583195 3.55117 1.11077 3.55904 1.43132 3.8893L5.06863 7.63683L12.2353 0.252938C12.5559 -0.0773196 13.0835 -0.0851939 13.4137 0.23535Z" fill="white"/>
            </svg>
          ) : (
            ''
          )}
          ДОдати у кошик
        </button>
      ) : (
        <button className={styles.wishlistItem__selectButton} disabled>
          ОБРАТИ РОЗМІР
        </button>
      )}
    </div>
  )
}

export const WishlistSidebar: FC = () => {
  const { isOpen, closeDrawer } = useWishlistDrawer()
  const { openDrawer: openCartDrawer } = useCartDrawer()
  const { is_supplier } = useContext(SupplierContext)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const queryParam = is_supplier ? Number(window.config.CUSTOMER_ID) : 0
  const productTypeParam = is_supplier ? 'supplier' : 'buyme'

  // Infinite scroll state
  const [currentPage, setCurrentPage] = useState(1)
  const [accumulatedItems, setAccumulatedItems] = useState<FavoriteProduct[]>([])
  const [hasMore, setHasMore] = useState(true)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)

  const { data: favoritesProducts, isLoading, isFetching } = useGetFavoriteMerchantQuery({
    merchant_id: queryParam,
    page: currentPage,
  }, {
    skip: !isOpen, // Only fetch when drawer is open
    refetchOnMountOrArgChange: true, // Force refetch when drawer reopens
  })

  const [postCartItem] = usePostCartItemMutation()
  const [removeFavorite] = useRemoveFavoriteMutation()
  const [fetchProducts, { data: popularProducts, isLoading: productsLoading }] = useFetchSupplierProductsListMutation()
  const { data: bestSellers, loading: bestSellersLoading } = useAppSelector(ProductSelectors.bestSellers)

  // Get cart items for count
  const { data: cartItems } = useGetCartItemsQuery({
    merchant_id: queryParam,
    per_page: 20,
    page: 1,
  })

  // Reset state when drawer opens
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1)
      setAccumulatedItems([])
      setHasMore(true)
    }
  }, [isOpen])

  useEffect(() => {
    if (favoritesProducts?.data && isOpen) {
      if (currentPage === 1) {
        setAccumulatedItems(favoritesProducts.data)
      } else {
        setAccumulatedItems(prev => {
          const newItems = [...prev, ...favoritesProducts.data]
          
return newItems
        })
      }

      if (favoritesProducts.meta) {
        const hasMorePages = currentPage < favoritesProducts.meta.last_page
        setHasMore(hasMorePages)
      }
    }
  }, [favoritesProducts, currentPage, isOpen])

  useEffect(() => {
    if (!isOpen || !hasMore || isFetching) {
      return
    }

    if (!scrollContainerRef.current || !loadMoreRef.current) {
      return
    }


    const options = {
      root: scrollContainerRef.current,
      rootMargin: '50px',
      threshold: 0.1,
    }

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !isFetching) {
        setCurrentPage(prev => prev + 1)
      }
    }, options)

    observerRef.current.observe(loadMoreRef.current)

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [isOpen, hasMore, isFetching, currentPage, accumulatedItems.length])

  useEffect(() => {
    if (is_supplier) {
      fetchProducts({
        customer_id: window.config.CUSTOMER_ID,
        category_id: Number(window.config.CATEGORY_ID),
      })
    } else {
      // @ts-ignore
      dispatch(getBestSellers())
    }
  }, [is_supplier, dispatch, fetchProducts])

  const handleOpenCart = () => {
    closeDrawer()
    openCartDrawer()
  }

  const handleRemoveFromWishlist = (productId: number) => {
    try {
      // Optimistically remove from UI immediately
      setAccumulatedItems(prev => prev.filter((item:any) =>
        (item.id || item?.product_id) !== productId
      ))

      // Make API call to remove from backend
      removeFavorite({
        product_type: productTypeParam,
        product_id: productId,
      })
    } catch (error: any) {
      console.error('Failed to remove item from wishlist:', error)

      // Extract error message from backend response
      const errorMessage = error?.data?.message || error?.data?.error || 'Помилка при видаленні з обраного'
      showNotification(ToastVersions.error, errorMessage)
    }
  }

  const isLoadingProducts = is_supplier ? productsLoading : bestSellersLoading
  const bestSellersProducts = is_supplier ? popularProducts?.data : bestSellers

  const handleAddToCart = async (productId: number, selectedSize: SizeOption | null): Promise<boolean> => {
    if (!selectedSize) return false

    try {
      const product_type = is_supplier ? 'supplier' : 'buyme'

      await postCartItem({
        product_type,
        product_id: productId,
        quantity: 1,
        product_option_value_id: selectedSize.product_option_value_id,
        warehouse_id: selectedSize.warehouse_id,
      }).unwrap()

      return true
    } catch (error: any) {
      console.error('Failed to add item to cart:', error)

      // Extract error message from backend response
      const errorMessage = error?.data?.message || error?.data?.error || 'Помилка при додаванні товару до кошика'
      showNotification(ToastVersions.error, errorMessage)

      return false
    }
  }

  const handleGoToWishlist = () => {
    closeDrawer()
    navigate(ERoutePaths.Wishlist)
  }

  return (
    <>
      <div className={`${styles.drawer} ${isOpen ? styles.open : ''}`}>
        {!isLoading && accumulatedItems.length === 0 ? (
          <>
            <div className={styles.header}>
              <CloseIcon onClick={closeDrawer} className={styles.header__closeIcon} />
              <div className={styles.header__icons}>
                <div className={styles.header__iconWrapper}>
                  <WishListIcon />
                  {(favoritesProducts?.meta?.total ?? 0) > 0 && (
                    <span className={styles.header__count}>{favoritesProducts?.meta.total}</span>
                  )}
                </div>
                <div className={styles.header__icons__cart} onClick={handleOpenCart}>
                  <div className={styles.header__iconWrapper}>
                    <ShoppingCartIcon />
                    {(cartItems?.meta?.total ?? 0) > 0 && (
                      <span className={styles.header__count}>{cartItems.meta.total}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.content}>
              <h1 className={styles.content__title}>
                ВІШЛІСТ
              </h1>
              <div className={styles.emptyState}>
                <div className={styles.emptyState__icon}>❤️‍🔥</div>
                <p className={styles.emptyState__text}>ВІШЛІСТ ПОРОЖНІЙ</p>
              </div>
              <ProductSlider
                productList={bestSellersProducts}
                loading={isLoadingProducts}
                is_bestsellers={true}
              />
            </div>
          </>
        ) : (
          <>
            <div className={styles.header}>
              <CloseIcon onClick={closeDrawer} className={styles.header__closeIcon} />
              <div className={styles.header__icons}>
                <div className={styles.header__iconWrapper}>
                  <WishListIcon />
                  {(favoritesProducts?.meta?.total ?? 0) > 0 && (
                    <span className={styles.header__count}>{favoritesProducts?.meta.total}</span>
                  )}
                </div>
                <div className={styles.header__icons__cart} onClick={handleOpenCart}>
                  <div className={styles.header__iconWrapper}>
                    <ShoppingCartIcon />
                    {(cartItems?.meta?.total ?? 0) > 0 && (
                      <span className={styles.header__count}>{cartItems.meta.total}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.content} ref={scrollContainerRef}>
              <h1 className={styles.content__title}>
                ВІШЛІСТ
              </h1>

              {isLoading && currentPage === 1 ? (
                <div className={styles.loading}>Завантаження...</div>
              ) : (
                <>
                  {accumulatedItems.map((item: any) => (
                    <WishlistItem
                      key={item.id || item.favorite_item_id}
                      item={item}
                      onAddToCart={handleAddToCart}
                      onRemoveFromWishlist={handleRemoveFromWishlist}
                    />
                  ))}

                  {/* Sentinel element for infinite scroll */}
                  {hasMore && (
                    <div ref={loadMoreRef} className={styles.loadMoreSentinel}>
                      {isFetching && currentPage > 1 && (
                        <div className={styles.loadingMore}>Завантаження...</div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className={styles.footer}>
              <button className={styles.footer__button} onClick={handleGoToWishlist}>
                ПЕРЕЙТИ ДО ОБРАНОГО
              </button>
            </div>
          </>
        )}
      </div>

      {isOpen && <div className={styles.overlay} onClick={closeDrawer} />}
    </>
  )
}
